import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO27005_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 27005:2022 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Information Security Risk Management — Guidelines for identifying,
            assessing, treating and monitoring information security risks.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 27005 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 27005 is an international standard that provides
              guidelines for information security risk management. It is part of
              the ISO/IEC 27000 family of standards, which focus on information
              security. ISO/IEC 27005 offers a structured approach to identify,
              assess, evaluate, and mitigate information security risks within
              an organization, ensuring that the integrity, availability, and
              confidentiality of critical data are protected.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              ISO/IEC 27005 helps businesses manage information security risks
              effectively by focusing on risk assessment, risk treatment, and
              risk monitoring. Achieving ISO/IEC 27005 certification
              demonstrates an organization’s commitment to proactive information
              security, reducing vulnerabilities, and enhancing overall data
              protection and cybersecurity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 27005 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27005 certification provides a comprehensive framework for
              managing information security risks within the context of an
              Information Security Management System (ISMS), such as the one
              outlined by ISO/IEC 27001. The standard provides organizations
              with the tools and methodology to:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Identify and assess information security risks",
                "Evaluate the potential impact and likelihood of these risks",
                "Develop effective risk mitigation strategies",
                "Establish ongoing monitoring and improvement of risk management processes",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mt-6">
              ISO/IEC 27005 is essential for businesses seeking to protect their
              data, IT systems, and critical information assets from evolving
              cyber threats, data breaches, and information security risks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 27005 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Risk Identification",
                desc: "ISO/IEC 27005 provides methodologies to identify information security risks across organizational processes, assets, and IT systems. This step ensures that organizations are aware of potential threats to their data, infrastructure, and operations.",
              },
              {
                icon: BarChart3,
                title: "Risk Assessment",
                desc: "The standard outlines a process for assessing the potential impact and likelihood of identified risks. This includes evaluating vulnerabilities and threats that could compromise confidentiality, integrity, or availability of information.",
              },
              {
                icon: Target,
                title: "Risk Treatment",
                desc: "ISO/IEC 27005 provides guidance on selecting and implementing appropriate risk treatment options to mitigate identified risks. This includes defining and applying controls that reduce the likelihood and impact of security threats.",
              },
              {
                icon: TrendingUp,
                title: "Ongoing Monitoring and Review",
                desc: "The standard emphasizes the need for continuous monitoring and regular reviews of risk management strategies to ensure their effectiveness over time. This ensures that the risk management process evolves with emerging threats.",
              },
              {
                icon: Globe,
                title: "Integration with ISMS",
                desc: "ISO/IEC 27005 is designed to work alongside other ISO/IEC 27000 series standards, particularly ISO/IEC 27001, for organizations implementing an Information Security Management System (ISMS). It integrates seamlessly into the ISMS framework to enhance the security posture of the organization.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO/IEC 27005 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Proactive Risk Management — ISO/IEC 27005 provides a structured approach to identifying and managing information security risks, enabling businesses to proactively protect their critical information assets and IT infrastructure.",
                "Data Protection and Cybersecurity — By following the guidelines of ISO/IEC 27005, businesses can implement effective data protection measures to safeguard sensitive information from cyberattacks, data breaches, and unauthorized access.",
                "Compliance with Global Standards — ISO/IEC 27005 helps organizations meet compliance requirements for data protection laws, such as GDPR (General Data Protection Regulation), CCPA (California Consumer Privacy Act), and other privacy regulations that mandate strict risk management practices.",
                "Enhanced Business Continuity — Effective risk management ensures that businesses can maintain business continuity by identifying potential disruptions early and implementing measures to prevent or mitigate them.",
                "Build Trust with Clients and Stakeholders — ISO/IEC 27005 certification demonstrates a commitment to managing information security risks and protecting data, building trust among customers, partners, and investors who value data privacy and cybersecurity.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO/IEC 27005 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 27005 provides guidelines for managing information
              security risks in a structured and consistent manner. The key
              components include:
            </p>
            <div className="space-y-4">
              {[
                "1. Risk Assessment Process — The standard outlines the risk assessment process, which includes risk identification, risk analysis, and risk evaluation. This step helps organizations understand the potential threats and vulnerabilities that could affect their information security.",
                "2. Risk Treatment Plans — ISO/IEC 27005 guides businesses in developing risk treatment plans, which outline specific actions and controls to mitigate identified risks. This may include security controls, policy changes, or technology upgrades.",
                "3. Risk Acceptance Criteria — The standard establishes criteria for accepting risks, ensuring that organizations only accept risks that align with their risk appetite and business objectives.",
                "4. Risk Monitoring and Review — ISO/IEC 27005 emphasizes the need for ongoing monitoring and continuous improvement of the risk management process to ensure that risks are effectively managed over time and that the organization can adapt to emerging threats.",
                "5. Integration with ISMS — The standard is designed to integrate seamlessly with other ISO/IEC 27001 processes, ensuring that information security risk management is a core element of an organization’s overall ISMS.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO/IEC 27005 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Reduced Information Security Risks",
                "Improved Compliance",
                "Business Continuity and Disaster Recovery",
                "Increased Customer Trust and Confidence",
                "Continuous Improvement",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO/IEC 27005 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO/IEC 27005 certification typically involves the
              following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Risk Management Framework Setup — Develop a structured risk management framework for identifying, assessing, and mitigating information security risks based on the guidelines provided by ISO/IEC 27005.",
                "2. Conduct a Risk Assessment — Identify and assess potential risks to confidentiality, integrity, and availability of your organization’s information. Evaluate the impact of these risks and develop risk treatment plans to mitigate them.",
                "3. Implement Risk Treatment Controls — Apply appropriate controls to treat the identified risks, including technical controls, policies, and procedures to prevent, detect, or reduce the risks.",
                "4. Document and Review — Document your risk management process and ensure that it aligns with ISO/IEC 27005 standards. Review the process regularly to ensure that it is effective and reflects any changes in the organization’s risk landscape.",
                "5. Third-Party Certification Audit — Engage an accredited certification body to conduct an audit of your risk management processes and verify compliance with ISO/IEC 27005 standards.",
                "6. Ongoing Monitoring and Continuous Improvement — Establish monitoring processes and conduct regular reviews to ensure that risk management efforts are continuously improved and adapted to emerging threats.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 27005 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert ISO/IEC 27005 certification
              consultancy services, helping businesses develop, implement, and
              optimize their information security risk management processes to
              enhance data protection and cybersecurity.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Risk Assessment and Gap Analysis",
                "Risk Treatment and Control Implementation",
                "ISMS Integration",
                "Documentation and Policy Development",
                "Third-Party Certification Audit Support",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO/IEC 27005 certification, ensuring
              effective information security risk management and regulatory
              compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO27005_Details;
